/**
 * WIDA Helpers — Single source of truth for WIDA levels, colors, and Can Do descriptors.
 * Used across all components for consistent WIDA-aligned display.
 */

// WIDA Performance Level colors
export const WIDA_COLORS = {
  1: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', ring: '#ef4444', fill: '#fca5a5' },
  2: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', ring: '#f97316', fill: '#fdba74' },
  3: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', ring: '#f59e0b', fill: '#fcd34d' },
  4: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', ring: '#22c55e', fill: '#86efac' },
  5: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300', ring: '#14b8a6', fill: '#5eead4' },
  6: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', ring: '#3b82f6', fill: '#93c5fd' },
};

// WIDA Performance Level names
export const WIDA_LEVEL_NAMES = {
  1: 'Entering',
  2: 'Emerging',
  3: 'Developing',
  4: 'Expanding',
  5: 'Bridging',
  6: 'Reaching',
};

// WIDA Can Do Descriptors — simplified, student-friendly versions
export const CAN_DO_DESCRIPTORS = {
  listening: {
    1: 'I can understand simple words and phrases when I hear them.',
    2: 'I can follow short, simple directions and understand key words in conversations.',
    3: 'I can understand the main idea of short talks and classroom discussions.',
    4: 'I can follow most classroom discussions and understand multi-step directions.',
    5: 'I can understand detailed explanations and academic discussions.',
    6: 'I can understand complex academic language at a grade-level standard.',
  },
  speaking: {
    1: 'I can say familiar words and short phrases.',
    2: 'I can ask and answer simple questions using short sentences.',
    3: 'I can describe familiar topics and participate in short conversations.',
    4: 'I can explain ideas clearly and give short presentations.',
    5: 'I can discuss academic topics in detail and support my opinions.',
    6: 'I can communicate fluently on academic topics at a grade-level standard.',
  },
  reading: {
    1: 'I can recognize familiar words and match them to pictures.',
    2: 'I can read short, simple sentences and find basic information.',
    3: 'I can identify main ideas and some details in short texts.',
    4: 'I can read and understand grade-level texts with some support.',
    5: 'I can analyze texts, compare ideas, and draw conclusions.',
    6: 'I can read and interpret complex academic texts at grade level.',
  },
  writing: {
    1: 'I can write or copy familiar words and label pictures.',
    2: 'I can write simple sentences about familiar topics.',
    3: 'I can write short paragraphs with a main idea and some details.',
    4: 'I can write organized paragraphs with supporting details.',
    5: 'I can write well-structured essays with evidence and analysis.',
    6: 'I can produce clear, detailed academic writing at grade level.',
  },
};

// Modality icons and labels
export const MODALITY_INFO = {
  listening: { label: 'Listening', short: 'L', iconName: 'Headphones' },
  speaking: { label: 'Speaking', short: 'S', iconName: 'Mic' },
  reading: { label: 'Reading', short: 'R', iconName: 'BookOpen' },
  writing: { label: 'Writing', short: 'W', iconName: 'PenTool' },
};

/**
 * Convert a raw score (0-100) to a WIDA level (1-6)
 */
export function scoreToWidaLevel(score) {
  if (!score || score <= 0) return 1;
  if (score < 20) return 1;
  if (score < 40) return 2;
  if (score < 55) return 3;
  if (score < 70) return 4;
  if (score < 85) return 5;
  return 6;
}

/**
 * Get the color scheme for a WIDA level
 */
export function getWidaColor(level) {
  return WIDA_COLORS[Math.min(6, Math.max(1, Math.round(level || 1)))];
}

/**
 * Get the Can Do descriptor for a modality and level
 */
export function getCanDo(modality, level) {
  const lvl = Math.min(6, Math.max(1, Math.round(level || 1)));
  return CAN_DO_DESCRIPTORS[modality]?.[lvl] || '';
}

/**
 * Get the level name (Entering, Emerging, etc.)
 */
export function getLevelName(level) {
  return WIDA_LEVEL_NAMES[Math.min(6, Math.max(1, Math.round(level || 1)))] || 'Entering';
}
